import type { Container } from "@egalregal/shared";
import { parentOf } from "@egalregal/shared";
import type { OwnCatalogEntry, SharedCatalogEntry } from "../../shared/api/requests";

/**
 * Display name of the active catalog entry for breadcrumb roots
 * (catalog-entry-sharing 09): own entries render `{name}`, shared entries
 * mirror the switcher pattern `{name}@{owner}`. Returns null unless more
 * than one entry is visible and the selection resolves — callers fall back
 * to the translated `topLevel` string.
 */
export function resolveEntryDisplayName(
  entries: readonly OwnCatalogEntry[],
  sharedWithMe: readonly SharedCatalogEntry[],
  selectedEntryId: string | undefined,
): string | null {
  if (entries.length + sharedWithMe.length <= 1) return null;
  if (selectedEntryId === undefined) return null;
  const own = entries.find((e) => e.id === selectedEntryId);
  if (own) return own.name;
  const shared = sharedWithMe.find((s) => s.entry.id === selectedEntryId);
  if (shared) return `${shared.entry.name}@${shared.owner}`;
  return null;
}

/**
 * The catalog entry a container belongs to, resolved locally from the
 * readable container union (ticket 12). Membership is derived from an entry's
 * roots, so walking the loaded parent links to the container's root and
 * matching that root against the own + shared root lists is enough — no
 * per-container server lookup.
 *
 * @param containers the readable container union (preferred) or loaded slice.
 * @param entries own catalog entries with their roots.
 * @param sharedWithMe shared entries with their roots (ticket 12).
 * @param containerId the container to place in an entry.
 * @param preferredEntryId the active entry, preferred when it also contains
 *   the container — mirrors the server's `entryContextOf` precedence so a
 *   search row never labels a different entry than the one an open scopes to.
 * @returns the entry id, or undefined when the container/root is not loaded.
 */
export function entryIdOfContainer(
  containers: readonly Container[],
  entries: readonly OwnCatalogEntry[],
  sharedWithMe: readonly SharedCatalogEntry[],
  containerId: string,
  preferredEntryId?: string,
): string | undefined {
  const rootId = rootIdOf(containers, containerId);
  if (!rootId) return undefined;
  const own = entries.filter((e) => e.roots.includes(rootId));
  const preferredOwn =
    preferredEntryId === undefined
      ? undefined
      : own.find((e) => e.id === preferredEntryId);
  const preferredShared =
    preferredEntryId === undefined
      ? undefined
      : sharedWithMe.find(
          (s) => s.entry.id === preferredEntryId && s.entry.roots?.includes(rootId),
        );
  // The active entry wins among every matching own + shared entry; own still
  // beats shared when the active entry does not contain the container.
  if (preferredOwn || preferredShared) return preferredEntryId;
  return own[0]?.id ?? sharedWithMe.find((s) => s.entry.roots?.includes(rootId))?.entry.id;
}

/**
 * Entry-rooted display label of a container (`Garage`, `Werkstatt@bob`), or
 * null when no entry label applies (single visible entry, container not in the
 * union). Search rows, gallery captions and the detail breadcrumb all resolve
 * the label through {@link resolveEntryDisplayName}, so they cannot diverge.
 *
 * @param containers the readable container union (preferred) or loaded slice.
 * @param entries own catalog entries with their roots.
 * @param sharedWithMe shared entries with their roots.
 * @param containerId the container whose entry label to resolve.
 * @param preferredEntryId the active entry, preferred when it also contains
 *   the container (same precedence as {@link entryIdOfContainer}).
 */
export function containerEntryLabel(
  containers: readonly Container[],
  entries: readonly OwnCatalogEntry[],
  sharedWithMe: readonly SharedCatalogEntry[],
  containerId: string,
  preferredEntryId?: string,
): string | null {
  const entryId = entryIdOfContainer(
    containers,
    entries,
    sharedWithMe,
    containerId,
    preferredEntryId,
  );
  return resolveEntryDisplayName(entries, sharedWithMe, entryId);
}

/** The top-level ancestor (root) of a container in the loaded union. */
function rootIdOf(containers: readonly Container[], containerId: string): string | undefined {
  let cursor: Container | undefined = containers.find((c) => c.id === containerId);
  while (cursor) {
    const parent = parentOf(containers, cursor.id);
    if (!parent) return cursor.id;
    cursor = parent;
  }
  return undefined;
}
